import { useMsal } from '@azure/msal-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type AssignableUser, type JobAssignments } from './client';

export interface AssignmentInput {
  type: 'REVIEWER' | 'INTERVIEWER';
  userId: string;
  applicationId?: string;
}

export function useAssignableUsers() {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['assignableUsers'],
    queryFn: () => apiFetch<AssignableUser[]>(instance, '/api/assignable-users'),
  });
}

export function useJobAssignments(jobId: string | undefined) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['jobAssignments', jobId],
    queryFn: () => apiFetch<JobAssignments>(instance, `/api/jobs/${jobId}/assignments`),
    enabled: Boolean(jobId),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, jobId: string) {
  qc.invalidateQueries({ queryKey: ['jobAssignments', jobId] });
  qc.invalidateQueries({ queryKey: ['application'] });
  qc.invalidateQueries({ queryKey: ['applications'] });
  qc.invalidateQueries({ queryKey: ['me'] });
}

export function useCreateAssignment(jobId: string) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignmentInput) =>
      apiFetch<{ assignmentId: string }>(instance, `/api/jobs/${jobId}/assignments`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateAll(qc, jobId),
  });
}

export function useDeleteAssignment(jobId: string) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      apiFetch(instance, `/api/jobs/${jobId}/assignments/${assignmentId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc, jobId),
  });
}
